/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { Col, Row } from "react-awesome-styled-grid";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Input, Label } from "@nordcloud/gnui";
import { FormGroup, Metadata } from "~/components";
import { ResourceGroupData } from "../constants";
import { FormData, FormField } from "../formConfig";
import { ResourceGroupDetailsSchema } from "./resourceGroupDetailsSchema";

type Props = {
  initialValues?: ResourceGroupData;
  onSubmit: SubmitHandler<FormData>;
  formId?: string;
};

export function ResourceGroupDetails({
  onSubmit,
  initialValues,
  formId,
}: Props) {
  const formMethods = useForm({
    resolver: zodResolver(ResourceGroupDetailsSchema),
    defaultValues: {
      [FormField.NAME]: initialValues?.details?.name ?? "",
      [FormField.DESCRIPTION]: initialValues?.details?.description ?? "",
      [FormField.METADATA]: initialValues?.details?.metadata ?? undefined,
    },
  });

  const { formState, register, handleSubmit } = formMethods;

  const submit: SubmitHandler<FormData> = (data) => {
    onSubmit({
      [FormField.NAME]: data.name,
      [FormField.DESCRIPTION]: data.description,
      [FormField.METADATA]: data.metadata,
    });
  };

  return (
    <>
      <Row justify="center">
        <Col sm={6} md={6} lg={8}>
          <Row>
            <Col>
              <FormProvider {...formMethods}>
                <form id={formId} onSubmit={handleSubmit(submit)}>
                  <FormGroup error={formState.errors[FormField.NAME]}>
                    <Label name="Group name" htmlFor="name" required />
                    <Input id="name" {...register("name")} />
                  </FormGroup>
                  <FormGroup error={formState.errors[FormField.DESCRIPTION]}>
                    <Label name="Description" htmlFor="description" />
                    <Input id="description" {...register("description")} />
                  </FormGroup>
                  <Metadata />
                </form>
              </FormProvider>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}
